import { useCallback, useState } from "react";
import { useReactFlow, MiniMap } from "@xyflow/react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Add01Icon,
  ZoomInAreaIcon,
  ZoomOutAreaIcon,
  Maximize01Icon,
  DashboardSquare01Icon,
  PlayIcon,
  Cancel01Icon,
} from "@hugeicons/core-free-icons";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CanvasToolbarProps {
  onAddNode: () => void;
  onRunPipeline: () => void;
  onCancelRun: () => void;
  isRunning: boolean;
}

export function CanvasToolbar({
  onAddNode,
  onRunPipeline,
  onCancelRun,
  isRunning,
}: CanvasToolbarProps) {
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  const [showMinimap, setShowMinimap] = useState(false);

  const handleZoomIn = useCallback(() => zoomIn({ duration: 200 }), [zoomIn]);
  const handleZoomOut = useCallback(
    () => zoomOut({ duration: 200 }),
    [zoomOut],
  );
  const handleFitView = useCallback(
    () => fitView({ duration: 300, padding: 0.2 }),
    [fitView],
  );

  return (
    <TooltipProvider>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 rounded-lg border border-border/50 dark:border-white/10 bg-card/80 px-1.5 py-1 shadow-sm backdrop-blur-sm"
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            {isRunning ? (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onCancelRun}
                className="gap-1.5 px-2.5 w-auto text-red-500 hover:text-red-600"
              >
                <HugeiconsIcon icon={Cancel01Icon} size={14} />
                <span className="text-xs">Cancel</span>
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onRunPipeline}
                className="gap-1.5 px-2.5 w-auto text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300"
              >
                <HugeiconsIcon icon={PlayIcon} size={14} />
                <span className="text-xs">Run</span>
              </Button>
            )}
          </TooltipTrigger>
          <TooltipContent side="top">
            {isRunning ? "Cancel pipeline run" : "Run pipeline"}
          </TooltipContent>
        </Tooltip>

        <div className="h-4 w-px bg-border/50" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onAddNode}
              className="gap-1.5 px-2.5 w-auto cursor-pointer"
            >
              <HugeiconsIcon icon={Add01Icon} size={14} />
              <span className="text-xs">Add Node</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            Add a new node to the workflow
          </TooltipContent>
        </Tooltip>

        <div className="h-4 w-px bg-border/50" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" onClick={handleZoomIn}>
              <HugeiconsIcon icon={ZoomInAreaIcon} size={14} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Zoom in</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" onClick={handleZoomOut}>
              <HugeiconsIcon icon={ZoomOutAreaIcon} size={14} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Zoom out</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" onClick={handleFitView}>
              <HugeiconsIcon icon={Maximize01Icon} size={14} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Fit workflow to view</TooltipContent>
        </Tooltip>

        <div className="h-4 w-px bg-border/50" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setShowMinimap((v) => !v)}
              className={showMinimap ? "bg-accent" : ""}
            >
              <HugeiconsIcon icon={DashboardSquare01Icon} size={14} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Show/hide minimap</TooltipContent>
        </Tooltip>
      </div>

      {showMinimap && (
        <MiniMap
          position="bottom-right"
          style={{ width: 140, height: 100 }}
          className="bottom-px! right-px!"
          maskColor="var(--minimap-mask)"
          maskStrokeColor="none"
          nodeColor="var(--color-muted-foreground)"
          nodeStrokeWidth={0}
          pannable
          zoomable
        />
      )}
    </TooltipProvider>
  );
}
